import { ChevronDown, ChevronUp } from "lucide-react";
import {
  useEffect,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
} from "react";
import {
  searchFilterToolbarDefaultClassNames,
  type SearchFilterToolbarClassNamesOverride,
} from "./search_filter_toolbar_defaults";
import type {
  SharedModalSlotRenderProps,
  SharedToolbarButtonProps,
} from "./toolbar_types";

export { searchFilterToolbarDefaultClassNames };
export type { SearchFilterToolbarClassNamesOverride };

export type SearchFilterToolbarShellProps = {
  labels: { filterBy: string; sortBy: string };
  classNames?: SearchFilterToolbarClassNamesOverride;
  Button: ComponentType<SharedToolbarButtonProps>;
  renderFilterModal: (props: SharedModalSlotRenderProps) => ReactNode;
  renderSortModal: (props: SharedModalSlotRenderProps) => ReactNode;
  onApplySearch: () => void | Promise<void>;
  /** Between filter cluster and sort cluster (e.g. job “create offer” link). */
  renderBetween?: (ctx: { desktopModalsOpen: boolean }) => ReactNode;
  /** Close the other mobile panel when opening one. */
  exclusiveMobilePanels?: boolean;
  /** Desktop panels close on outside click (job app). */
  closeDesktopOnOutsideClick?: boolean;
};

export function SearchFilterToolbarShell({
  labels,
  classNames,
  Button,
  renderFilterModal,
  renderSortModal,
  onApplySearch,
  renderBetween,
  exclusiveMobilePanels = false,
  closeDesktopOnOutsideClick = false,
}: SearchFilterToolbarShellProps) {
  const cn = { ...searchFilterToolbarDefaultClassNames, ...classNames };

  const [showMobileFilter, setShowMobileFilter] = useState(false);
  const [showMobileSort, setShowMobileSort] = useState(false);
  const [showDesktopFilter, setShowDesktopFilter] = useState(false);
  const [showDesktopSort, setShowDesktopSort] = useState(false);

  const filterWrapRef = useRef<HTMLDivElement>(null);
  const sortWrapRef = useRef<HTMLDivElement>(null);

  const showDesktopModals = showDesktopFilter || showDesktopSort;

  useEffect(() => {
    if (!closeDesktopOnOutsideClick) return;
    const close = (e: MouseEvent) => {
      const n = e.target as Node;
      if (filterWrapRef.current && !filterWrapRef.current.contains(n)) {
        setShowDesktopFilter(false);
      }
      if (sortWrapRef.current && !sortWrapRef.current.contains(n)) {
        setShowDesktopSort(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [closeDesktopOnOutsideClick]);

  const runApply = () => {
    void onApplySearch();
  };

  const toggleMobileFilter = () => {
    if (exclusiveMobilePanels) setShowMobileSort(false);
    setShowMobileFilter((v) => !v);
  };

  const toggleMobileSort = () => {
    if (exclusiveMobilePanels) setShowMobileFilter(false);
    setShowMobileSort((v) => !v);
  };

  const toggleDesktopFilter = () => {
    if (exclusiveMobilePanels) setShowDesktopSort(false);
    setShowDesktopFilter((v) => !v);
  };

  const toggleDesktopSort = () => {
    if (exclusiveMobilePanels) setShowDesktopFilter(false);
    setShowDesktopSort((v) => !v);
  };

  return (
    <>
      <div className={cn.toolbarOuter}>
        <div
          className={`${cn.toolbarInner} ${showDesktopModals ? cn.toolbarInnerWhenDesktopModalOpen : ""}`}
        >
          <div
            ref={closeDesktopOnOutsideClick ? filterWrapRef : undefined}
            className="flex flex-wrap items-center gap-2 md:gap-4"
          >
            <Button
              className={`${cn.mobileFilterButton}${showMobileFilter && cn.mobileFilterButtonWhenOpen ? ` ${cn.mobileFilterButtonWhenOpen}` : ""}`}
              variant="alternate"
              onClick={toggleMobileFilter}
            >
              {labels.filterBy}
              {showMobileFilter ? (
                <ChevronUp className="ml-2 hidden h-4 w-4 md:block" />
              ) : (
                <ChevronDown className="ml-2 hidden h-4 w-4 md:block" />
              )}
            </Button>
            <Button
              className={`${cn.desktopFilterButton} ${showDesktopSort ? cn.desktopFilterButtonHiddenWhenSortOpen : ""}${showDesktopFilter && cn.desktopFilterButtonWhenOpen ? ` ${cn.desktopFilterButtonWhenOpen}` : ""}`}
              variant="alternate"
              onClick={toggleDesktopFilter}
            >
              {labels.filterBy}
              {showDesktopFilter ? (
                <ChevronUp className="ml-2 hidden h-4 w-4 md:block" />
              ) : (
                <ChevronDown className="ml-2 hidden h-4 w-4 md:block" />
              )}
            </Button>
            {showDesktopFilter &&
              renderFilterModal({
                className: cn.desktopFilterModal,
                isBlocking: false,
                onClose: () => setShowDesktopFilter(false),
                onSubmit: () => {
                  runApply();
                  setShowDesktopFilter(false);
                },
              })}
          </div>

          {renderBetween?.({ desktopModalsOpen: showDesktopModals })}

          <div
            ref={closeDesktopOnOutsideClick ? sortWrapRef : undefined}
            className="flex flex-wrap items-center justify-end gap-2 md:gap-4"
          >
            <Button
              className={`${cn.mobileSortButton}${showMobileSort && cn.mobileSortButtonWhenOpen ? ` ${cn.mobileSortButtonWhenOpen}` : ""}`}
              variant="alternate"
              onClick={toggleMobileSort}
            >
              {labels.sortBy}
              {showMobileSort ? (
                <ChevronUp className="ml-2 hidden h-4 w-4 md:block" />
              ) : (
                <ChevronDown className="ml-2 hidden h-4 w-4 md:block" />
              )}
            </Button>
            <Button
              className={`${cn.desktopSortButton} ${showDesktopFilter ? cn.desktopSortButtonHiddenWhenFilterOpen : ""}${showDesktopSort && cn.desktopSortButtonWhenOpen ? ` ${cn.desktopSortButtonWhenOpen}` : ""}`}
              variant="alternate"
              onClick={toggleDesktopSort}
            >
              {labels.sortBy}
              {showDesktopSort ? (
                <ChevronUp className="ml-2 hidden h-4 w-4 md:block" />
              ) : (
                <ChevronDown className="ml-2 hidden h-4 w-4 md:block" />
              )}
            </Button>
            {showDesktopSort &&
              renderSortModal({
                className: cn.desktopSortModal,
                isBlocking: false,
                onClose: () => setShowDesktopSort(false),
                onSubmit: () => {
                  runApply();
                  setShowDesktopSort(false);
                },
              })}
          </div>
        </div>
      </div>

      {showMobileFilter &&
        renderFilterModal({
          className: cn.mobileFilterModal,
          isBlocking: true,
          onClose: () => setShowMobileFilter(false),
          onSubmit: () => {
            runApply();
            setShowMobileFilter(false);
          },
        })}

      {showMobileSort &&
        renderSortModal({
          className: cn.mobileSortModal,
          isBlocking: true,
          onClose: () => setShowMobileSort(false),
          onSubmit: () => {
            runApply();
            setShowMobileSort(false);
          },
        })}
    </>
  );
}
